import React, { useState, useEffect } from 'react';
import { 
  Share2, 
  ExternalLink, 
  Copy, 
  Check, 
  Download, 
  Eye, 
  Sparkles, 
  Globe, 
  MessageCircle, 
  Twitter, 
  Linkedin, 
  AlertCircle, 
  CheckCircle2, 
  HelpCircle,
  RefreshCw,
  Layers,
  ArrowRight,
  Info
} from 'lucide-react';
import { ROUTE_SEO_MAP, getSeoConfigForPath, DOMAIN } from '../utils/seoMetadata';

interface SocialPreviewDiagnosticProps {
  currentPath?: string;
  showToast: (msg: string) => void;
  onNavigate?: (path: string) => void;
}

const SocialPreviewDiagnosticComponent: React.FC<SocialPreviewDiagnosticProps> = ({
  currentPath = window.location.pathname || '/',
  showToast,
  onNavigate
}) => {
  const [selectedRoute, setSelectedRoute] = useState<string>(() => {
    return currentPath.split('?')[0] || '/';
  });

  const [activePlatform, setActivePlatform] = useState<'x' | 'linkedin' | 'whatsapp' | 'google' | 'tags'>('x');
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedImage, setCopiedImage] = useState(false);
  const [copiedTags, setCopiedTags] = useState(false);

  // Sync selected route when currentPath changes from outside
  useEffect(() => {
    const clean = currentPath.split('?')[0] || '/';
    if (ROUTE_SEO_MAP[clean]) {
      setSelectedRoute(clean);
    }
  }, [currentPath]);

  const seoConfig = getSeoConfigForPath(selectedRoute);
  const canonicalUrl = `${DOMAIN}${seoConfig.path === '/' ? '' : seoConfig.path}`;
  const title = seoConfig.title;
  const description = seoConfig.description;
  const imageUrl = seoConfig.image;

  // Title & description validation scores
  const titleLength = title.length;
  const descLength = description.length;

  const isTitleOptimal = titleLength >= 40 && titleLength <= 65;
  const isDescOptimal = descLength >= 110 && descLength <= 165;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(canonicalUrl);
    setCopiedLink(true);
    showToast('📋 Canonical share link copied to clipboard!');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyImageUrl = () => {
    navigator.clipboard.writeText(imageUrl);
    setCopiedImage(true);
    showToast('🖼️ OG Image URL copied to clipboard!');
    setTimeout(() => setCopiedImage(false), 2000);
  };

  const handleOpenLinkedInInspector = () => {
    const inspectorUrl = `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(canonicalUrl)}`;
    window.open(inspectorUrl, '_blank', 'noopener,noreferrer');
    showToast('🔗 Opening LinkedIn Post Inspector in new tab...');
  };

  const handleOpenTwitterPreview = () => {
    // X Web Intent to test share card preview
    const xIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(canonicalUrl)}`;
    window.open(xIntent, '_blank', 'noopener,noreferrer');
  };

  const handleOpenWhatsAppShare = () => {
    const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} - ${canonicalUrl}`)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const generateRawHtmlTags = () => {
    return `<!-- Primary Meta Tags -->
<title>${title}</title>
<meta name="title" content="${title}">
<meta name="description" content="${description}">
<link rel="canonical" href="${canonicalUrl}">

<!-- Open Graph / Facebook / LinkedIn / WhatsApp -->
<meta property="og:type" content="website">
<meta property="og:url" content="${canonicalUrl}">
<meta property="og:title" content="${title}">
<meta property="og:description" content="${description}">
<meta property="og:image" content="${imageUrl}">
<meta property="og:image:width" content="1200">
<meta property="og:image:height" content="630">
<meta property="og:site_name" content="AzuraOS">

<!-- Twitter -->
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:site" content="@AzuraOSapp">
<meta name="twitter:url" content="${canonicalUrl}">
<meta name="twitter:title" content="${title}">
<meta name="twitter:description" content="${description}">
<meta name="twitter:image" content="${imageUrl}">`;
  };

  const handleCopyTags = () => {
    navigator.clipboard.writeText(generateRawHtmlTags());
    setCopiedTags(true);
    showToast('📑 HTML Meta Tags copied to clipboard!');
    setTimeout(() => setCopiedTags(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* 1. Header Banner & Diagnostics Overview */}
      <div className="p-5 sm:p-6 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/20 flex items-center justify-center text-[#14B8A6]">
                <Share2 size={16} />
              </div>
              <h3 className="text-base sm:text-lg font-display font-bold text-white tracking-tight">
                Social Media & Open Graph Diagnostic Engine
              </h3>
              <span className="px-2 py-0.5 text-[10px] font-mono font-bold uppercase bg-[#14B8A6]/10 text-[#14B8A6] rounded-md border border-[#14B8A6]/30">
                LIVE CRAWLER READY
              </span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed max-w-2xl">
              Inspect how AzuraOS routes render when shared across X / Twitter, LinkedIn, WhatsApp, Facebook, and search engines. Every view features dedicated 1200×630 high-resolution Open Graph branding.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleCopyLink}
              className="px-3 py-2 bg-[#0C0E0F] hover:bg-[#1E2325] text-gray-200 hover:text-white border border-[#2A2F31] rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
              title="Copy active URL"
            >
              {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              <span>Copy Share URL</span>
            </button>

            <button
              onClick={handleOpenLinkedInInspector}
              className="px-3 py-2 bg-[#0A66C2]/15 hover:bg-[#0A66C2]/25 text-[#70B5F9] border border-[#0A66C2]/40 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
              title="Inspect with LinkedIn Post Inspector to verify or clear LinkedIn cache"
            >
              <Linkedin size={14} />
              <span>LinkedIn Inspector</span>
              <ExternalLink size={12} className="opacity-70" />
            </button>
          </div>
        </div>

        {/* 2. Route Selector Pills */}
        <div className="pt-2 border-t border-[#1F2426]">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Layers size={13} className="text-[#14B8A6]" />
            <span>Select Route to Diagnose:</span>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
            {Object.entries(ROUTE_SEO_MAP).map(([route, config]) => {
              const isSelected = selectedRoute === route;
              return (
                <button
                  key={route}
                  onClick={() => setSelectedRoute(route)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                    isSelected
                      ? 'bg-[#14B8A6] text-black font-bold shadow-md'
                      : 'bg-[#0C0E0F] text-gray-400 hover:text-white hover:bg-[#1A1F21] border border-[#1F2426]'
                  }`}
                >
                  <span>{route === '/' ? 'Home (/)' : route}</span>
                  <span className={`text-[9px] px-1 py-0.2 rounded font-mono ${isSelected ? 'bg-black/20 text-black' : 'bg-gray-800 text-gray-400'}`}>
                    {config.badge}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. SEO Health & Audit Metrics Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Title Health */}
        <div className="p-4 bg-[#151819] border border-[#1F2426] rounded-xl space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400 font-medium">Title Tag Length</span>
            <span className={`font-mono text-xs font-bold px-1.5 py-0.5 rounded ${
              isTitleOptimal ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
            }`}>
              {titleLength} / 60 chars
            </span>
          </div>
          <p className="text-[11px] text-gray-400">
            {isTitleOptimal ? '✓ Ideal length for search & social previews.' : 'Optimal range is 40–60 characters.'}
          </p>
        </div>

        {/* Description Health */}
        <div className="p-4 bg-[#151819] border border-[#1F2426] rounded-xl space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400 font-medium">Meta Description</span>
            <span className={`font-mono text-xs font-bold px-1.5 py-0.5 rounded ${
              isDescOptimal ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
            }`}>
              {descLength} / 160 chars
            </span>
          </div>
          <p className="text-[11px] text-gray-400">
            {isDescOptimal ? '✓ High click-through rate copy length.' : 'High click-through rate copy length.'}
          </p>
        </div>

        {/* Image Spec Health */}
        <div className="p-4 bg-[#151819] border border-[#1F2426] rounded-xl space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400 font-medium">Open Graph Image</span>
            <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              1200 × 630 PNG
            </span>
          </div>
          <p className="text-[11px] text-gray-400">
            ✓ 1.91:1 high-DPI aspect ratio for Twitter large summary & LinkedIn cards.
          </p>
        </div>
      </div>

      {/* 4. Platform Preview Container */}
      <div className="bg-[#151819] border border-[#1F2426] rounded-2xl overflow-hidden shadow-xl">
        {/* Platform Selector Tabs */}
        <div className="flex items-center justify-between border-b border-[#1F2426] bg-[#0C0E0F] px-4 py-2.5 overflow-x-auto">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActivePlatform('x')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activePlatform === 'x'
                  ? 'bg-white text-black shadow'
                  : 'text-gray-400 hover:text-white hover:bg-[#1A1F21]'
              }`}
            >
              <Twitter size={14} />
              <span>X (Twitter Card)</span>
            </button>

            <button
              onClick={() => setActivePlatform('linkedin')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activePlatform === 'linkedin'
                  ? 'bg-[#0A66C2] text-white shadow'
                  : 'text-gray-400 hover:text-white hover:bg-[#1A1F21]'
              }`}
            >
              <Linkedin size={14} />
              <span>LinkedIn Post</span>
            </button>

            <button
              onClick={() => setActivePlatform('whatsapp')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activePlatform === 'whatsapp'
                  ? 'bg-[#25D366] text-black shadow'
                  : 'text-gray-400 hover:text-white hover:bg-[#1A1F21]'
              }`}
            >
              <MessageCircle size={14} />
              <span>WhatsApp / iMessage</span>
            </button>

            <button
              onClick={() => setActivePlatform('google')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activePlatform === 'google'
                  ? 'bg-[#4285F4] text-white shadow'
                  : 'text-gray-400 hover:text-white hover:bg-[#1A1F21]'
              }`}
            >
              <Globe size={14} />
              <span>Google Search (SERP)</span>
            </button>

            <button
              onClick={() => setActivePlatform('tags')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activePlatform === 'tags'
                  ? 'bg-[#14B8A6] text-black shadow'
                  : 'text-gray-400 hover:text-white hover:bg-[#1A1F21]'
              }`}
            >
              <span className="font-mono">&lt;/&gt;</span>
              <span>Raw Meta Tags</span>
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <a
              href={imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#14B8A6] hover:underline flex items-center gap-1"
            >
              <Eye size={12} />
              <span>Open Image</span>
            </a>
          </div>
        </div>

        {/* Platform Specific Preview Renderers */}
        <div className="p-4 sm:p-8 bg-[#0D1011] flex flex-col items-center justify-center min-h-[380px]">
          
          {/* A. X / Twitter Card Preview (Large Summary Image) */}
          {activePlatform === 'x' && (
            <div className="max-w-lg w-full bg-black border border-[#2F3336] rounded-2xl overflow-hidden shadow-2xl font-sans text-left transition-all hover:border-[#3F4448]">
              {/* Post Author Mockup */}
              <div className="p-3.5 flex items-center gap-2.5 border-b border-[#2F3336]/40 bg-[#0A0C0D]">
                <div className="w-9 h-9 rounded-full bg-[#151819] border border-[#14B8A6]/30 flex items-center justify-center font-bold text-[#14B8A6] text-xs">
                  A
                </div>
                <div className="leading-tight">
                  <div className="text-xs font-bold text-white flex items-center gap-1">
                    <span>AzuraOS</span>
                    <span className="text-[#1D9BF0]">✓</span>
                  </div>
                  <div className="text-[11px] text-gray-500">@AzuraOSapp • Just now</div>
                </div>
              </div>

              {/* Tweet Content */}
              <div className="px-3.5 py-2.5 text-xs text-gray-200">
                Sovereign clarity over every income stream. Real-time net profit margins and AI Co-Founder intelligence. Check out the link below 👇
              </div>

              {/* Large Image Card */}
              <div className="border border-[#2F3336] mx-3.5 mb-3.5 rounded-xl overflow-hidden bg-[#16181C] group cursor-pointer">
                <div className="relative aspect-[1200/630] w-full overflow-hidden bg-[#0C0E0F]">
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-md rounded text-[10px] font-mono text-gray-300">
                    azuraos.app
                  </div>
                </div>
                <div className="p-3 bg-[#16181C] space-y-1">
                  <div className="text-[11px] text-gray-400 font-mono">azuraos.app</div>
                  <div className="text-sm font-bold text-white line-clamp-1 leading-snug">
                    {title}
                  </div>
                  <div className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                    {description}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* B. LinkedIn Post Inspector Mockup */}
          {activePlatform === 'linkedin' && (
            <div className="max-w-lg w-full bg-[#1B1F23] border border-[#2E343A] rounded-xl overflow-hidden shadow-2xl font-sans text-left">
              <div className="p-3.5 flex items-center gap-2.5 bg-[#161A1D] border-b border-[#2E343A]">
                <div className="w-10 h-10 rounded-full bg-[#14B8A6]/20 border border-[#14B8A6]/40 flex items-center justify-center font-bold text-[#14B8A6] text-sm">
                  A
                </div>
                <div>
                  <div className="text-xs font-bold text-white flex items-center gap-1">
                    AzuraOS • Sovereign Founder OS
                  </div>
                  <div className="text-[10px] text-gray-400">1,420 followers • Promoted</div>
                </div>
              </div>

              <div className="p-3 text-xs text-gray-300 leading-relaxed">
                Take sovereign control of your multi-stream business. Track bookings, expenses, tax readiness, and net margins in one place.
              </div>

              {/* LinkedIn Large Image Preview */}
              <div className="bg-[#161A1D] border-t border-b border-[#2E343A] cursor-pointer group">
                <div className="aspect-[1200/630] w-full overflow-hidden bg-[#0C0E0F]">
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-3 bg-[#161A1D] space-y-1">
                  <div className="text-xs font-bold text-white line-clamp-1 group-hover:text-[#70B5F9] transition-colors">
                    {title}
                  </div>
                  <div className="text-[11px] text-gray-400 flex items-center justify-between">
                    <span>azuraos.app</span>
                    <span className="text-[10px] text-gray-500 font-mono">1200x630</span>
                  </div>
                </div>
              </div>

              <div className="p-2.5 flex items-center justify-around text-xs text-gray-400 border-t border-[#2E343A]/40 bg-[#161A1D]">
                <span>👍 Like</span>
                <span>💬 Comment</span>
                <span>🔄 Repost</span>
                <span>✈️ Send</span>
              </div>
            </div>
          )}

          {/* C. WhatsApp / Telegram / iMessage Chat Bubble Preview */}
          {activePlatform === 'whatsapp' && (
            <div className="max-w-md w-full bg-[#1F2C34] rounded-2xl p-2.5 shadow-2xl border border-[#2A3942] font-sans text-left">
              <div className="text-[11px] text-[#25D366] font-bold px-2 py-0.5 mb-1 flex items-center gap-1">
                <MessageCircle size={12} />
                <span>WhatsApp Message Preview</span>
              </div>

              <div className="bg-[#111B21] rounded-xl overflow-hidden border border-[#222E35] space-y-0">
                <div className="aspect-[1200/630] w-full overflow-hidden bg-[#0C0E0F]">
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3 bg-[#111B21] space-y-1">
                  <div className="text-xs font-bold text-gray-100 line-clamp-1">
                    {title}
                  </div>
                  <div className="text-[11px] text-gray-400 line-clamp-2 leading-relaxed">
                    {description}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono pt-1">
                    azuraos.app
                  </div>
                </div>
              </div>

              <div className="p-2 text-xs text-[#53BDEB] underline truncate">
                {canonicalUrl}
              </div>
            </div>
          )}

          {/* D. Google Search (SERP Snippet) */}
          {activePlatform === 'google' && (
            <div className="max-w-lg w-full bg-[#202124] border border-[#303134] rounded-xl p-4 shadow-2xl font-sans text-left space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-[#151819] border border-[#14B8A6]/40 flex items-center justify-center text-[10px] text-[#14B8A6] font-bold">
                  A
                </div>
                <div className="text-xs text-[#BDC1C6] leading-tight">
                  <div className="font-medium text-white">AzuraOS</div>
                  <div className="text-[11px] text-[#9AA0A6]">{canonicalUrl}</div>
                </div>
              </div>

              <div className="text-base font-medium text-[#8AB4F8] hover:underline cursor-pointer leading-snug">
                {title}
              </div>

              <div className="text-xs text-[#BDC1C6] leading-relaxed">
                {description}
              </div>

              <div className="pt-2 flex items-center gap-2 text-[11px] text-[#9AA0A6]">
                <span className="px-1.5 py-0.5 bg-[#303134] rounded">Free Trial</span>
                <span className="px-1.5 py-0.5 bg-[#303134] rounded">No Credit Card</span>
                <span className="px-1.5 py-0.5 bg-[#303134] rounded">Google AI BYOK</span>
              </div>
            </div>
          )}

          {/* E. Raw HTML Meta Tags */}
          {activePlatform === 'tags' && (
            <div className="w-full max-w-2xl bg-[#090B0C] border border-[#1F2426] rounded-xl p-4 text-left space-y-3 font-mono">
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span className="font-bold text-[#14B8A6]">Generated Open Graph & Twitter Card &lt;head&gt; tags:</span>
                <button
                  onClick={handleCopyTags}
                  className="px-2.5 py-1 bg-[#14B8A6] text-black font-bold rounded text-xs flex items-center gap-1 cursor-pointer"
                >
                  {copiedTags ? <Check size={12} /> : <Copy size={12} />}
                  <span>{copiedTags ? 'Copied' : 'Copy All'}</span>
                </button>
              </div>

              <pre className="text-[11px] text-gray-300 overflow-x-auto p-3 bg-[#050607] rounded-lg border border-[#161A1C] leading-relaxed">
                {generateRawHtmlTags()}
              </pre>
            </div>
          )}

        </div>

        {/* 5. Bottom Quick Actions & Verification Links */}
        <div className="p-4 bg-[#111416] border-t border-[#1F2426] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="text-gray-400 flex items-center gap-2">
            <Info size={14} className="text-[#14B8A6] shrink-0" />
            <span>
              Dynamic Open Graph images are served in real-time from <code className="text-gray-300 font-mono">{imageUrl}</code>
            </span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              onClick={handleCopyImageUrl}
              className="px-3 py-1.5 bg-[#151819] hover:bg-[#1E2325] text-gray-300 hover:text-white border border-[#2A2F31] rounded-lg font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
            >
              {copiedImage ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
              <span>Copy Image Link</span>
            </button>

            <button
              onClick={() => {
                if (onNavigate) {
                  onNavigate(selectedRoute);
                  showToast(`Navigated to ${selectedRoute}`);
                }
              }}
              className="px-3 py-1.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-lg flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
            >
              <span>Visit Page</span>
              <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SocialPreviewDiagnostic = React.memo(SocialPreviewDiagnosticComponent);

